import { equals } from 'ramda';
import { take, fork, cancel } from 'redux-saga/effects';
import { createSelectorCreator, defaultMemoize } from 'reselect';

import type { NotifyMessage, NotifyOptions, WithNotify } from 'store/types';

type NotifiableAction = {
  type: string;
  payload?: unknown;
  error?: boolean;
  meta?: object;
};

type NotifyPayload = {
  error?: NotifyMessage;
  message?: NotifyMessage;
  originalResponse?: {
    message?: NotifyMessage;
  };
};

export const notify = <A extends unknown[], R extends NotifiableAction>(
  actionCreator: (...args: A) => R,
  title: ((action: R) => string) | string,
  message: ((action: R) => string) | string = '',
  duration: number | null | undefined = undefined,
) => {
  return (...args: A): WithNotify<R> => {
    const action = actionCreator(...args);

    let notifyMessage: NotifyMessage | undefined;
    if (typeof message === 'function') {
      notifyMessage = message(action);
    } else {
      if (message) {
        notifyMessage = message;
      } else if (typeof action.payload === 'string') {
        notifyMessage = action.payload;
      } else if (typeof action.payload === 'object') {
        const obj: NotifyPayload = action.payload || {};
        //Why obj.originalResponse?.message ???: For safety if obj was created using destructed FetchError.
        //* FetchError.message is not an 'own' property, and this is not obvious. So if destructured, message will be undefined
        //* So fallback is to look at the originalResponse?.message
        notifyMessage =
          (obj.error || obj.message) ?? obj.originalResponse?.message;
      }
    }

    const opts: NotifyOptions = {
      status:
        action.error || action.type.indexOf('ERROR') !== -1
          ? 'error'
          : 'success',
      message: notifyMessage,
      title: typeof title === 'function' ? title(action) : title,
      duration: duration,
    };
    return {
      ...action,
      meta: {
        ...(action.meta || {}),
        __NOTIFY__: opts,
      },
    };
  };
};

/**
 * @see https://stackoverflow.com/a/53465495
 * use this to conditionally cancel a saga based on some key in the action
 * eg. cancel multiple calls for same operation id but don't cancel if the id is different
 */
export const takeLatestDeep = (patternOrChannel, identifier, saga, ...args) =>
  fork(function* () {
    const lastTasks = {};

    while (true) {
      const action = yield take(patternOrChannel);
      const index = identifier(action);

      if (lastTasks[index]) {
        yield cancel(lastTasks[index]);
      }

      lastTasks[index] = yield fork(saga, ...args.concat(action));
    }
  });

/*
A custom reselector that does a deep object comparison to determine if a reselector needs to be recomputed.

This is ideal for selectors that are dependent on complicated state objects, such as entities.

WARNING:
THIS CAN BE AN EXPENSIVE COMPUTATION.
IT IS NOT A CATCH-ALL SOLUTION AND SHOULD BE A LAST RESORT.
*/
export const createDeepMemoizedSelector = createSelectorCreator(
  defaultMemoize,
  equals,
);
